import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { unpublishProduct } from '@dukanti/shared';
import type { PublishProductInput, PublishProductResult, ProductWithImages } from '@dukanti/shared';
import { supabase } from '@/lib/supabase';
import { productKeys } from './use-products';

/**
 * Unpublish a single product via `publish_product(uuid, false)`.
 * Mirror of {@link usePublishProduct} for the inverse transition —
 * the RPC skips the store-published and inventory gates so the only
 * expected failure modes are permission, not-found, or network.
 *
 * ## Cache shape contract (LOAD-BEARING)
 *
 * Same merge-setQueryData contract as publish: read the existing
 * cached ProductWithImages (if any), merge the RPC row's scalar
 * columns over it, preserve `images`. The cache-miss branch returns
 * undefined and relies on list invalidation.
 *
 * ## List invalidation — DELTA vs F-004 `useUnpublishStore`
 *
 * Unlike `useUnpublishStore` (which deliberately skips child
 * invalidation per F-004 Decision 10 step 4b because an unpublish
 * leaves child product rows untouched), `useUnpublishProduct` DOES
 * invalidate the list because the single product's `published`
 * column flips. The list-row draft badge is driven by
 * `!product.published`, so the grid MUST refetch to render the
 * flipped state.
 *
 * ## Error behavior
 *
 * Errors re-thrown; the dialog runs `mapPublishProductError(err)`.
 */
export function useUnpublishProduct(): UseMutationResult<
  PublishProductResult,
  unknown,
  PublishProductInput
> {
  const queryClient = useQueryClient();

  return useMutation<PublishProductResult, unknown, PublishProductInput>({
    mutationFn: (input) => unpublishProduct(supabase, input),
    onSuccess: (row, variables) => {
      queryClient.setQueryData<ProductWithImages | undefined>(
        productKeys.detail(variables.productId),
        (existing) => (existing ? { ...existing, ...row } : undefined),
      );

      // List invalidation uses the RPC row's store_id — matches the
      // publish hook so a single mutation path can flip either way.
      void queryClient.invalidateQueries({
        queryKey: productKeys.list(row.store_id),
      });
    },
  });
}
